// These will go away once bridge exports types
import {
  AggregateComposition,
  AggregateEntity,
  BfmProposalAnalysisResponseV3,
  LinkedEntity,
  RuleSeverity,
} from '@blk/aw-bff-typescript-models';
import {
  BuyListItem,
  DateAsString,
  OptimizationContext,
  SecurityValuationOverride,
  Trade as TradeApb,
} from '@blk/awt-aladdinportfoliobuilderserver';
import { EventTypes } from './AWEvents';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing any, fix later
export type ProposalGeneratorInputs = { [key: string]: any };

export enum PortfolioTag {
  ACCOUNT = 'ACCOUNT',
  AGGREGATE = 'AGGREGATE',
  BENCHMARK = 'BENCHMARK',
  ENTITLED_MODEL = 'ENTITLED_MODEL',
  MODEL = 'MODEL',
}

export type Trade = TradeApb;

export interface AWGenerationInputs {
  buyList: BuyListItem[];
  parameters: ProposalGeneratorInputs;
  securityValuationOverrides: SecurityValuationOverride[];
}

export type ProposalType = 'MANUAL' | 'AUTO';

export interface AWProposalV2 {
  accountMetadata: { [key: string]: string };
  analysisDate: DateAsString;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing any
  annotations?: { [key: string]: any };
  createdBy?: string;
  createdFrom: AggregateEntity[];
  creationTimestamp: DateAsString | undefined;
  currency: string;
  entity: AggregateEntity;
  generationInputs?: AWGenerationInputs;
  lastUpdatedAnalysisTimestamp?: number;
  lastUpdatedBy: string;
  lastUpdatedTimestamp: DateAsString | undefined;
  linkedEntitySelections: LinkedEntity[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing any
  metadata: { [key: string]: any };
  name?: string;
  optimizationContext?: OptimizationContext;
  origin: 'SYSTEM' | 'USER';
  originalAggregate: AggregateComposition;
  originalNav?: number;
  passed: boolean;
  proposalId: string;
  proposedAggregate: AggregateComposition;
  proposedNav?: number;
  reason?: string;
  rules: AWRule[];
  snapshotId?: string;
  source: 'CLIENT' | 'CUSTOM' | 'UNKNOWN';
  trades?: Trade[];
  type: ProposalType;
}

export interface UpdateProposalEntryErrorStatus {
  /**
   * The status code, which should be an enum value of HttpStatus code
   */
  code: number;

  /**
   * A developer-facing error message, which should be in English. Any
   * user-facing error message should be localized and sent in the
   * the details field, or localized by the client.
   */
  message: string;
}

export interface AWProposalEntryAndErrorStatus {
  entry: AWProposalEntry;
  status: UpdateProposalEntryErrorStatus;
}

export interface AWProposalEntry {
  accountMetadata: { [key: string]: string };
  analysisDate: DateAsString;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing any
  annotations?: { [key: string]: any };
  assignee?: string;
  createdBy: string;
  createdFrom: AggregateEntity[];
  creationTimestamp: DateAsString;
  currency: string;
  entity: AggregateEntity;
  entryId: string;
  expiryTimestamp?: DateAsString;
  lastLog: AWProposalEntryLog;
  lastUpdatedAnalysisTimestamp?: DateAsString;
  lastUpdatedBy: string;
  lastUpdatedProposalTimestamp: DateAsString;
  lastUpdatedTimestamp: DateAsString;
  logs: AWProposalEntryLog[];
  metadata: { [key: string]: string };
  order?: AWProposalOrder;
  origin?: 'SYSTEM' | 'USER';
  originalNav?: number;
  passed: boolean;
  proposalId: string;
  proposalPurpose: string;
  proposedNav?: number;
  rules: AWRule[];
  snapshotId?: string;
  underlyingPortfolioIds: string[];
}

export type AWProposalEntryParams = {
  proposalId: string;
  pkg: string;
};

export interface MinimalAWProposalEntry {
  entryId: string;
  lastLog?: {
    workflowStatus?: string;
  };
  lastUpdatedTimestamp?: DateAsString;
}

export interface AWProposalOrder {
  createdBy: string;
  creationTimestamp: DateAsString;
  delivered: boolean;
  entity: AggregateEntity;
  entryId: string;
  lastUpdatedBy: string;
  lastUpdatedTimestamp: DateAsString;
}

export interface AWProposalEntryLog {
  assignee?: string;
  comment?: string;
  creationTimestamp?: DateAsString;
  entryId: string;
  fixedComment?: string;
  id?: string;
  user?: string;
  workflowStatus?: string;
}

export enum AWRuleSeverity {
  CRITICAL = 'Critical',
  WARNING = 'Warning',
}

export interface AWRule {
  category?: string;
  comment?: string;
  id: string;
  passed: boolean;
  severity?: RuleSeverity;
  shortName?: string;
}

export interface FacetPivotPair {
  firstValue?: object;
  secondValue?: object;
}

export interface FacetCategory {
  count?: number;
  label?: string;
}

export interface FacetPivotCategory {
  count?: number;
  label?: FacetPivotPair;
}

export type FacetDefinitionType = 'categorical' | 'range';

export interface FacetDefinition {
  end?: number;
  field?: FieldDefinition;
  gap?: number;
  minCount?: number;
  start?: number;
  type?: FacetDefinitionType;
}

export interface FacetPivotDefinition {
  firstField?: FieldDefinition;
  secondField?: FieldDefinition;
}

export interface FacetPivotResult {
  /**
   * Represents the count of this particular field/value combination
   */
  count: number;

  /**
   * Represents the groupBy key for this pivot
   */
  field: string;

  /**
   * Represents the nested facet pivot results
   * <p>
   * Each {@link FacetPivotResult} can reference another {@link FacetPivotResult}
   * if there are multiple facet pivots (multiple layers of group by)
   */
  pivot: FacetPivotResult[];

  /**
   * Represents the value of the grouping at this node
   */
  value: string | number;
}

export interface FacetResult {
  categories: FacetCategory[];
  fieldName: string;
}

export interface FilterDefinitionFilterRange {
  end: number | string;
  start: number | string;
}

export interface FilterDefinition {
  categories?: (string | boolean | null)[];
  field: FieldDefinition;
  isNegate?: boolean;
  /**
   * @deprecated Use isNegate instead. this field is deprecated as per the latest (swagger version)[https://bwn.blackrock.com/awportfolio/swagger-ui/index.html#/portfolios/search_1].
   */
  negate?: boolean;
  range?: FilterDefinitionFilterRange;
}

export type OperationType = 'AND' | 'OR';

export interface FilterParams {
  filters: FilterDefinition[];
  operationType?: OperationType;
}

export type FieldDefinitionFieldType = 'string' | 'number';

export interface FieldDefinition {
  name: string;
  type?: FieldDefinitionFieldType;
}

export interface SortDefinition {
  field: FieldDefinition;
  orderBy: string;
}

export interface SearchMetadata {
  cursor?: string;
  numFound: number;
  remainingItemCount?: number;
}

export interface ResourceList<T> {
  items: T[];
  metadata: SearchMetadata;
}

// mapped to com.brs.aw.commons.models.search.SearchResponse
export interface SearchResponse<T> extends ResourceList<T> {
  facetFields: FacetResult[];

  facetPivots: FacetPivotResult[];

  facetRanges: FacetResult[];
}

// mapped to com.brs.aw.commons.models.search.SearchParams
export interface SearchRequest {
  /**
   * the cursor option should be set when retrieving more results from the server.
   * Since this value is server defined, clients may only use the cursor value from a previous
   * query result with identical query parameters (except for the value of continue)
   * and the server may reject a cursor value it does not recognize.
   */
  cursor?: string;

  /**
   * A list of facet pivot definitions to group objects/documents by
   * The ordering of the facet pivots matters in that the first item will be the outermost grouping
   * while the last item in the list will be the innermost grouping.
   */
  facetPivots?: FieldDefinition[];

  /**
   * A list of facet definitions to group objects/documents by
   */
  facets?: FacetDefinition[];
  /**
   * A list of filter params
   */
  filterParams?: FilterParams[];

  /**
   * an integer representing the maximum number of elements to return
   */
  limit?: number;

  /**
   * This translates to the raw user input for typeahead search
   */
  searchTerm?: string;

  /**
   * A list of fields to populate in returned objects
   * Note, the wildcard field, '*' may be an acceptable special character to denote that
   * all fields of the object/document should be returned.
   */
  selects?: FieldDefinition[];

  /**
   * A list of sort definitions to sort objects/documents by
   */
  sorts?: SortDefinition[];

  /**
   * @deprecated
   * Prefer tag in url params as per latest swagger documents.
   */
  tag?: PortfolioTag;

  /**
   * The list of string fields to boost for searching against the {@link SearchParams#getSearchTerm()}
   */
  typeaheadFields?: FieldDefinition[];
}

// used for both /proposal-entries/bulkGet and /proposals/bulkGet
export interface BulkGetRequest {
  ids: string[];
}

export interface AssignProposalEntriesRequest {
  filters?: { [key: string]: (string | boolean | null)[] };
  limit?: number;
}

export interface EntriesWithNewLog {
  entry: AWProposalEntry;
  newLog: AWProposalEntryLog;
}

export interface ProposalEntryBulkUpdateResponse {
  failedEntries: AWProposalEntryAndErrorStatus[];
  successfulEntries: AWProposalEntry[];
}

export interface AWEventAggregate {
  tag: string;
  value: string;
}

export interface AWEventAccount {
  tag: string;
  value: string;
}

export interface AWEventData {
  accounts: AWEventAccount[];
  aggregates?: AWEventAggregate[];
  analysisId?: string;
  filename?: string;
  language?: string;
  proposalId?: string;
  reportAttributes?: string[];
  tag: string;
  value: string;
}
export interface ReleaseOrdersPayload {
  package: string;
  releasedBy: string;
  timestamp: DateAsString;
}

export interface AlertStatusUpdatedData {
  aggIDTag: string;
  aggIDValue: string;
  alertName: string;
  alertStatus: string;
  alertType: 'RULE';
  comment?: string;
  fixedComment?: string;
  multiAlertID?: string;
  snoozedUntilDate?: string;
}

export interface AlertStatusUpdatedRequestBody {
  data: AlertStatusUpdatedData;
  pkg: string;
  type: EventTypes.ALERT_STATUS_UPDATED;
}

export interface AWRuleDefinition {
  category: string;
  id: string;
  severity: AWRuleSeverity;
  shortName?: string;
}

export type ProposalEntryState =
  | 'UNASSIGNED'
  | 'ASSIGNED'
  | 'SAVED_FOR_LATER'
  | 'REJECTED'
  | 'APPROVED'
  | 'EXPIRED'
  | 'APPROVED_DELIVERED'
  | 'APPROVED_FAILED'
  | 'REJECTED_DELIVERED'
  | 'REJECTED_FAILED'
  | 'REVIEW_NEEDED'
  | 'ARCHIVED';

export interface AWProposalPRDConfiguration {
  /**
   * Identifies states that will be triggering OMS integration (e.g ["Approved", "Rejected"])
   */
  generateOrderStates: ProposalEntryState[];
  /**
   * Defines a list of purposes that is in scope of prd workflow (e.g ["TRADING"])
   */
  proposalPurposes: string[];

  /**
   * Allows user to define different names for each state described in state machine
   *   E.g: {
   *       "UNASSIGNED": "Unassigned",
   *       "ASSIGNED": "Assigned",
   *       "SAVED_FOR_LATER": "Saved for Later"
   *   }
   */
  states: { [state in ProposalEntryState]: string };

  /**
   * Roles to set users with perm to use PRD (e.g ["ProposalReview"])
   */
  viewRoles: string[];

  writeRoles: string[];
}

// ADO Epic 1562438 - Additional controls for proposal editability
export enum ProposalLockState {
  CORE = 'CORE',
  FULL = 'FULL',
  NONE = 'NONE',
}
export interface ProposalStatusLockType {
  proposalLock: ProposalLockState;
  supplementLocks: string[];
}

export interface ProposalStatusConfig {
  color: string;
  fixedComments: string[];
  hideFreeComment: boolean;
  locked: boolean;
  lockType?: ProposalStatusLockType;
  removeSupplements?: string[];
  roles: string[];
  title: string;
}

export enum AssignedToCurrentUser {
  ASSIGNED_TO_CURRENT_USER = 'ASSIGNED_TO_CURRENT_USER',
  NO_CHECK = 'NO_CHECK',
  NOT_ASSIGNED_TO_CURRENT_USER = 'NOT_ASSIGNED_TO_CURRENT_USER',
}

export interface ProposalDestinationStatusPredicate {
  assignedToCurrentUser: AssignedToCurrentUser;
  disableOnFailedRuleSeverities: string[];
  roles: string[];
}

export interface ProposalDestinationStatusAction {
  generateOrders: boolean;
  metadataKeysToRemove: string[];
  publishProposal: boolean;
  removeSupplements: string[];
  syncResolvedOrders: boolean;
}

export interface ProposalDestinationStatusConfig {
  actions: ProposalDestinationStatusAction;
  fixedCommentRequired: boolean;
  fixedComments: string[];
  predicates: ProposalDestinationStatusPredicate;
  status: string;
}

export interface WorkflowControl {
  destinationStatuses: ProposalDestinationStatusConfig[];
  lockType: ProposalStatusLockType;
  status: string;
}

export interface TypeLevelConfiguration {
  permission: {
    readRoles: string[];
    writeRoles: string[];
  };
  proposalType: string;
  workflowControls: WorkflowControl[];
}

export interface AWProposalConfiguration {
  groupSettings?: {
    groupKey: string;
    groupTypeKey: string;
  };

  /**
   * Used to enforce the PRD State Machine rules and blocks
   */
  prd: AWProposalPRDConfiguration;

  /**
   * list of supervisor roles that can override the proposal rules
   */
  proposalRuleOverridesSupervisorRoles: string[];

  /**
   * Mapping of AWPA ruleNames to Properties like 'Category' and 'Severity'
   *
   * TODO, remove this field once "rules" is removed from <PKG>.awProposalConfiguration.json {@link https://webster.bfm.com/jira/browse/AWM-20734}
   */
  rules: AWRuleDefinition[];
  /**
   * Order of rules severity
   *
   * Change this field to optional as it will be removed from <PKG>.awProposalConfiguration.json  {@link https://webster.bfm.com/jira/browse/AWM-20734}
   * TODO, remove this field once "rulesSeverityHierarchy" is removed from <PKG>.awProposalConfiguration.json
   */
  rulesSeverityHierarchy?: string[];

  statuses?: ProposalStatusConfig[];

  typeLevelConfigurations?: TypeLevelConfiguration[];
}

export interface ProposalWithAnalysis {
  analysis?: BfmProposalAnalysisResponseV3; // might have to deprecate this at some point
  description?: string;
  proposal: AWProposalV2;
}

export interface ProposalOptions {
  dataOptions: {
    takeSnapshot: boolean;
    skipAnalysis: boolean;
  };
}

export interface UpdateProposalRequest {
  options?: ProposalOptions;
  proposalWithAnalysisList: ProposalWithAnalysis[];
}

export interface ShareProposalRequest {
  proposalId: string;
  users: string[];
}

export interface ShareProposalResponse {
  failures: string[];
  successes: string[];
}

export interface BulkShareProposalsRequest {
  proposalIds: string[];
  users: string[];
}

export interface BulkShareProposalsResponse {
  errorMessage: string;
  successfulProposalIds: string[];
}

export interface SendProposalRequest {
  proposal: AWProposalV2;
  users: string[];
}

export interface SendProposalResponse {
  failures: string[];
  successes: { [user: string]: string };
}

interface Error {
  errorMessage: string;
  errorType: string;
}

export interface Status {
  code: number;
  details: Error[];
  message: string;
}

export interface ProposalWithEntryAndAnalysis {
  analysis?: BfmProposalAnalysisResponseV3;
  entry?: AWProposalEntry;
  proposal: AWProposalV2;
  status?: Status;
}

export interface ProposalOutput {
  failedProposals: ProposalWithEntryAndAnalysis[];
  successfulProposals: ProposalWithEntryAndAnalysis[];
}

/**
 * AW Performance API post request data shape
 */
export interface AWPerformanceRequest {
  analysisDate: DateAsString;
  includeBenchmark?: boolean;
  pkg?: string;
  purpose?: string;
}

/**
 * AW Performance Official API post request data shape
 */
export interface AWPerformanceOfficialRequest {
  analysisDate: DateAsString[];
}

/** AW Performance API response data shapes */
/**
 * AW Performance API aggregates data shapes
 */
export interface AWPerformanceAggregatesResponse {
  aggIdTag: string;
  aggIdValue: string;
  analysisDate: DateAsString;
  periodResponse: AWPerformanceAggregatesPeriod[];
  pkg: string;
  runType?: string;
}

/**
 * AW Performance API post request data shape
 */
export interface AWPerformanceCustomDateRequest {
  aggIdTag: string | null;
  aggIdValue: string | null;
  includeBenchmark?: boolean;
  periodEndDate: DateAsString;
  periodStartDate: DateAsString;
  pkg?: string;
  purpose?: string;
}

export interface AWPerformanceCustomDatePostResponse {
  aggIdTag: string;
  aggIdValue: string;
  id: string;
  lastUpdatedTime: string;
  message: string | null;
  periodEndDate: string;
  periodStartDate: string;
  pkg: string;
  status: string;
  user: string;
}

export interface AWPerformanceAccountDayZeroRequest {
  analysisDate: string;
  ids: string[];
  pkg: string;
}

export interface AWPerformanceAccountDayZeroResponse {
  dailySnapshotStartDate: string;
  monthlySnapshotStartDate: string;
}

export interface AWPerformanceCustomDateGetResponse {
  aggIdTag: string;
  aggIdValue: string;
  id: string;
  lastUpdatedTime: string;
  message: string | null;
  periodEndDate: string;
  periodResponse?:
    | null
    | {
        period: string;
        periodStartDate: DateAsString;
        performance: {
          averageCapitalOfficialNAV: number;
          averageCapitalBottomUp: number;
          pnl: {
            price: number;
            income: number;
            trade: number;
            fx: number;
            taxesAndFees: number;
            totalGross: number;
            totalNet: number;
          };
          returnsMoneyWeighted: {
            price: number;
            income: number;
            trade: number;
            totalGross: number;
            totalNet: number;
            totalOfficialNav: number;
            fx: number;
          };
          returnsTimeWeighted?: {
            totalGross: number;
            totalNet: number;
            totalOfficialNav: number;
          };
        };
        decomposition: AWPerformanceDecompositionSecurity[];
        breakdownDecomposition: AWPerformanceBreakdownDecomposition;
      }[];
  periodStartDate: string;
  pkg: string;
  status: string;
  user: string;
}

export interface AWPerformanceOfficialAggregatesResponse {
  performanceResponses: AWPerformanceAggregatesResponse[];
}

export interface AWPerformanceAggregatesPeriod {
  performance: {
    averageCapitalOfficialNAV: number;
    pnl: AWPerformanceAggregatesPeriodPerformancePnL;
    returnsMoneyWeighted: AWPerformanceAggregatesPeriodPerformanceReturnsMoneyWeighted;
    returnsTimeWeighted?: AWPerformanceAggregatesPeriodPerformanceReturnsTimeWeighted;
  };
  period: string;
  periodStartDate: DateAsString;
}

export interface AWPerformanceAggregatesPeriodPerformancePnL {
  fx: number;
  income: number;
  price: number;
  taxesAndFees: number;
  totalGross: number;
  totalNet: number;
  trade: number;
}

export interface AWPerformanceAggregatesPeriodPerformanceReturnsMoneyWeighted {
  fx: number;
  income: number;
  price: number;
  totalGross: number;
  totalNet: number;
  totalOfficialNav: number;
  trade: number;
}

export interface AWPerformanceAggregatesPeriodPerformanceReturnsTimeWeighted {
  totalGross: number;
  totalNet: number;
  totalOfficialNav: number;
}

/**
 * Custom Aggregate Views AW Performance API post request data shape
 */
export interface AWPerformanceCustomAggregateAnalysisPreDefinedPeriodsRequest {
  accounts: string[];
  aggIdTag: string;
  aggIdValue: string;
  analysisDate: DateAsString;
  includeBenchmark?: boolean;
  pkg?: string;
  purpose?: string;
}

export interface AWPerformanceCustomAggregateAnalysisPreDefinedPeriodsResponse {
  aggIdTag: string;
  aggIdValue: string;
  analysisDate: DateAsString;
  id: string;
  lastUpdatedTime: DateAsString;
  periodResponse?: [
    {
      period: string;
      periodStartDate: DateAsString;
      performance: {
        averageCapitalBottomUp: number;
        averageCapitalOfficialNAV: number;
        pnl: {
          income: number;
          price: number;
          trade: number;
          fx: number;
          totalGross: number;
          totalOfficialNav: number;
          totalOfficialNavNet: number;
          residual: number;
          taxesAndFees: number;
          totalNet: number;
        };
        returnsMoneyWeighted: {
          income: number;
          price: number;
          fx: number;
          trade: number;
          totalGross: number;
          totalNet: number;
          totalOfficialNav: number;
          totalOfficialNavNet: number;
          residual: number;
        };
      };
      decomposition: AWPerformanceDecompositionSecurity[];
      breakdownDecomposition: AWPerformanceBreakdownDecomposition;
    },
  ];
  pkg: string;
  status: string;
  user: string;
}

/**
 * AW Performance API aggregates decomposition security
 */
export interface AWPerformanceDecompositionSecurityResponse {
  aggIdTag: string;
  aggIdValue: string;
  analysisDate: DateAsString;
  periodResponse: AWPerformanceDecompositionSecurityPeriod[];
  pkg: string;
}

export interface AWPerformanceDecompositionSecurityPeriod {
  decomposition: AWPerformanceDecompositionSecurity[];
  performance: {
    averageCapitalBottomUp: number;
    pnl: {
      price: number;
      income: number;
      trade: number;
      fx: number;
      taxesAndFees: number;
      totalGross: number;
      totalNet: number;
    };
    returnsMoneyWeighted: {
      price: number;
      income: number;
      trade: number;
      totalGross: number;
      totalNet: number;
      totalOfficialNav: number;
      fx: number;
    };
    returnsTimeWeighted?: {
      totalGross: number;
      totalNet: number;
      totalOfficialNav: number;
    };
  };
  period: string;
  periodStartDate: DateAsString;
}

export interface AWPerformanceDecompositionSecuritySectors {
  [key: string]: string;
}

export interface AWPerformanceDecompositionSecurity {
  accountId: string;
  cusip: string;
  pnl: {
    fx: number;
    income: number;
    price: number;
    trade: number;
    total: number;
  };
  returnsMoneyWeighted: {
    fx: number;
    income: number;
    price: number;
    trade: number;
    total: number;
  };
  sectors: AWPerformanceDecompositionSecuritySectors;
  securityStandaloneReturn: number | null;
}

/**
 * AW Performance API aggregates decomposition breakdown
 */
export interface AWPerformanceDecompositionBreakdownResponse {
  aggIdTag: string;
  aggIdValue: string;
  analysisDate: DateAsString;
  benchmarkId?: string;
  periodResponse?: BreakDownPeriodResponse[];
  pkg: string;
}

export interface BreakDownPeriodResponse {
  decomposition?: AWPerformanceBreakdownDecomposition;
  performance: BreakdownPerformance;
  period: string;
  periodStartDate: DateAsString;
}

interface BreakdownPerformance {
  benchmarkReturn?: number;
  pnl: BreakdownPnl | null;
  returnsMoneyWeighted: BreakdownReturns;
}

export interface AWPerformanceBreakdownDecomposition {
  [breakdown: string]: AWPerformanceDecompositionBreakdown[];
}

export interface BreakdownPnl {
  fx: number;
  income: number;
  price: number;
  taxesAndFees?: number;
  totalGross: number;
  totalNet?: number;
  totalOfficialNav?: number;
  trade: number;
}

export interface BreakdownReturns {
  fx: number;
  income: number;
  price: number;
  totalGross: number;
  totalNet?: number;
  totalOfficialNav?: number;
  trade: number;
}

/*
 * TODO Currently there is a bug in this response where an optional field will be returned as null instead of being ommitted.
 * So our optional fields should include "| null" for the time being.
 * The fix to the API is expected with the May 2021 release.
 */
export interface AWPerformanceDecompositionBreakdown {
  benchmarkReturn?: number | null;
  benchmarkReturnContribution?: number | null;
  pnl?: BreakdownPnl | null;
  returnsMoneyWeighted?: BreakdownReturns | null;
  sector: string;
}

export interface BulkGetWithEntryAndAnalysisRequest {
  ids: string[];
}
